'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["editor", "onInsert", "onEdit", "mode", "onModeChange", "onTokenRemove", "eventKey"];
import { useState, useEffect, useCallback, useRef } from 'react';
import PersonalizePopoverBodyInsert from 'rich-text-lib/components/personalize/PersonalizePopoverBodyInsert';
import PersonalizePopoverBodyBranch from 'rich-text-lib/components/personalize/PersonalizePopoverBodyBranch';
import { PopoverType, PopoverBodyMode } from 'rich-text-lib/constants/popover';
import { getPropertyValues } from '../utils/token';
import useInlineTarget from '../utils/hooks/useInlineTarget';
import TinyMcePopover from './TinyMcePopover';
import withPopoverModeState from './withPopoverModeState';
import { INLINE_TARGET_ATTR } from '../constants/inlineTarget';
import { jsx as _jsx } from "react/jsx-runtime";
function PersonalizePopoverManager(props) {
  const {
      editor,
      onInsert,
      onEdit,
      mode,
      onModeChange,
      onTokenRemove,
      eventKey
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const [popoverType, setPopoverType] = useState(PopoverType.INSERT);
  const {
    inlineTarget,
    setInlineTarget,
    initInlineTarget
  } = useInlineTarget(editor, null);
  const [property, setProperty] = useState();
  const lastTarget = useRef(inlineTarget);
  const lastPopoverType = useRef(popoverType);

  // Store values in immutable refs for useEffect optimization
  useEffect(() => {
    lastTarget.current = inlineTarget;
    lastPopoverType.current = popoverType;
  });
  const handleRemove = useCallback(({
    restoreSelection
  } = {}) => {
    if (lastTarget.current) {
      setInlineTarget(null, {
        restoreSelection
      });
      setProperty();
    }
  }, [setInlineTarget]);
  const handleInsert = (...args) => {
    handleRemove();
    onInsert(...args);
  };
  const handleEdit = (...args) => {
    handleRemove();
    onEdit(...args);
  };
  const handleTokenRemove = () => {
    handleRemove();
    onTokenRemove(inlineTarget);
  };
  useEffect(() => {
    const handleOpenPopover = () => {
      if (lastTarget.current) {
        handleRemove({
          restoreSelection: true
        });
      } else {
        setPopoverType(PopoverType.INSERT);
        initInlineTarget();
      }
    };
    editor.on('OpenPersonalizePopover', handleOpenPopover);
    return () => editor.off('OpenPersonalizePopover', handleOpenPopover);
  }, [editor, initInlineTarget, handleRemove]);
  useEffect(() => {
    const handleHsNodeChange = ({
      element
    }) => {
      if (!element) {
        handleRemove();
        return;
      }
      if (element.hasAttribute(INLINE_TARGET_ATTR)) {
        // This ignores the insert popover scenario (and for non-ptokens)
        return;
      }
      onModeChange(PopoverBodyMode.PREVIEW);
      if (element !== lastTarget.current) {
        setInlineTarget(null);
        setPopoverType(PopoverType.EDIT);
        setProperty(getPropertyValues(element));
        setInlineTarget(element);
      }
    };
    editor.on(`hsNodeChange:${eventKey}`, handleHsNodeChange);
    return () => editor.off(`hsNodeChange:${eventKey}`, handleHsNodeChange);
  }, [editor, setInlineTarget, handleRemove, onModeChange, eventKey]);
  const isInsert = popoverType === PopoverType.INSERT;
  let PopoverContent;
  let popoverContentProps = {};
  if (isInsert) {
    PopoverContent = PersonalizePopoverBodyInsert;
    popoverContentProps = {
      onInsert: handleInsert,
      onClose: handleRemove
    };
  } else {
    PopoverContent = PersonalizePopoverBodyBranch;
    popoverContentProps = {
      mode,
      onModeChange,
      onEdit: handleEdit,
      onTokenRemove: handleTokenRemove
    };
  }
  return /*#__PURE__*/_jsx(TinyMcePopover, {
    editor: editor,
    "data-test-id": "personalize-browser-popover",
    open: !!inlineTarget,
    target: inlineTarget,
    placement: "bottom",
    onRemove: handleRemove,
    animateOnToggle: false,
    renderContent: () => /*#__PURE__*/_jsx(PopoverContent, Object.assign({}, rest, popoverContentProps, {
      apiClient: editor.settings.apiClient,
      cacheKey: editor.id,
      property: property,
      tooltipClassName: "mce-token-tooltip"
    }))
  });
}
export default withPopoverModeState(PersonalizePopoverManager);